<template>
  <div class="scenario-detail">
    <!-- Empty State -->
    <div v-if="!scenario" class="no-selection has-text-centered p-6">
      <span class="icon is-large has-text-grey-light">
        <i class="fas fa-chess-board fa-3x"></i>
      </span>
      <p class="mt-4 has-text-grey">Select an example to view details</p>
    </div>

    <!-- Scenario Content -->
    <template v-else>
      <!-- Header -->
      <div
        class="panel-heading is-flex is-justify-content-space-between is-align-items-center p-2"
      >
        <div class="is-flex is-align-items-center">
          <span class="title is-6 mb-0">{{ scenario.title }}</span>
          <b-tag
            v-if="scenario.isPuzzle"
            class="ml-2"
            type="is-danger is-light"
          >
            🧩 Puzzle
          </b-tag>
          <b-tag v-if="scenario.isHomework" type="is-warning is-light">
            📚 Homework
          </b-tag>
        </div>
        <b-tooltip
          :label="scenario.updatedAt | asDateTime"
          position="is-left"
          type="is-light"
        >
          <span class="has-text-grey is-size-7">
            Updated {{ scenario.updatedAt | distanceFromNow }}
          </span>
        </b-tooltip>
      </div>

      <!-- Content -->
      <div class="scenario-content">
        <!-- Stats -->
        <div class="stats-container">
          <b-tag
            v-if="scenario.order"
            type="is-info is-light"
            size="is-small"
            rounded
          >
            # Order: {{ scenario.order }}
          </b-tag>

          <PuzzleLevelBadge
            v-if="scenario.puzzleLevel"
            :level="scenario.puzzleLevel"
          />

          <TopicStatusBadge :status="scenario.status" />
        </div>

        <!-- Board Preview and Moves -->
        <div class="board-and-moves-container">
          <div class="board-container">
            <BoardSnapshot :pgn="scenario.pgn" :board-size="320" />
          </div>
          <div class="moves-container">
            <div class="moves-header">
              <b-icon icon="chess-pawn" size="is-small" />
              {{ moveCount }} moves
            </div>
            <pre class="moves-text">{{
              movesString || "No moves available"
            }}</pre>
          </div>
        </div>

        <!-- PGN Headers -->
        <div v-if="scenario.pgn" class="pgn-preview">
          <div class="pgn-header">Game Information</div>
          <pre class="pgn-text">{{
            simplifiedHeader || "No header information available"
          }}</pre>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <b-button
            v-if="canEdit"
            type="is-warning"
            icon-left="edit"
            expanded
            @click="$emit('edit', scenario)"
          >
            Edit
          </b-button>
          <b-button
            type="is-info"
            icon-left="chalkboard-teacher"
            expanded
            @click="$emit('start')"
          >
            Coach
          </b-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import BoardSnapshot from "@/components/training/explorer/molecules/BoardSnapshot.vue";
import PuzzleLevelBadge from "../atoms/PuzzleLevelBadge.vue";
import TopicStatusBadge from "../atoms/TopicStatusBadge.vue";
import { PgnParser } from "vue-chess-shared/pgn/PgnParser";

export default {
  name: "ScenarioPreview",

  components: {
    BoardSnapshot,
    PuzzleLevelBadge,
    TopicStatusBadge,
  },

  props: {
    scenario: {
      type: Object,
      default: null,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    simplifiedHeader() {
      if (!this.scenario || !this.scenario.pgn) {
        return "";
      }
      return PgnParser.getSimplifiedHeaders(this.scenario.pgn);
    },

    movesString() {
      if (!this.scenario || !this.scenario.pgn) {
        return "";
      }
      const { moves } = PgnParser.splitHeadersAndMoves(this.scenario.pgn);
      return moves || "";
    },

    moveCount() {
      if (!this.scenario || !this.scenario.pgn) {
        return 0;
      }
      return PgnParser.getMoveCount(this.scenario.pgn);
    },
  },
};
</script>

<style scoped>
.scenario-detail {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
  border: 1px solid #dbdbdb;
}

.no-selection {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.panel-heading {
  background-color: #f5f5f5;
  border-bottom: 2px solid #dbdbdb;
  min-height: 52px;
  flex-shrink: 0;
}

.scenario-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem;
  gap: 0.5rem;
  min-height: 0; /* allow content to shrink */
  overflow-y: auto; /* enable scrolling when needed */
}

.stats-container {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.board-and-moves-container {
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: flex-start;
  flex-shrink: 0; /* prevent shrinking */
}

.board-container {
  display: flex;
  justify-content: center;
  flex-shrink: 0; /* prevent shrinking */
}

.moves-container {
  flex: 1;
  min-width: 150px;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  background-color: #f8f8f8;
  border-radius: 4px;
  padding: 0.5rem;
}

.moves-header {
  font-weight: bold;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dbdbdb;
}

.moves-text {
  flex: 1;
  overflow-y: auto;
  font-family: monospace;
  font-size: 0.85rem;
  line-height: 1.4;
  text-align: left;
  padding: 0;
  margin: 0;
}

.pgn-preview {
  width: 100%;
  background-color: #f8f8f8;
  border-radius: 4px;
  padding: 0.5rem;
  flex: 1; /* take remaining space */
  min-height: 0; /* allow shrinking */
  display: flex;
  flex-direction: column;
}

.pgn-header {
  font-weight: bold;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dbdbdb;
}

.pgn-text {
  font-family: monospace;
  font-size: 0.75rem;
  line-height: 1.4;
  padding: 0;
  margin: 0;
  background-color: white;
  border-radius: 4px;
  flex: 1; /* take remaining space */
  overflow-y: auto;
  text-align: left;
}

.action-buttons {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.5rem;
  padding-top: 0.5rem;
  background-color: white;
  border-top: 1px solid #dbdbdb;
  flex-shrink: 0; /* prevent shrinking */
  position: sticky;
  bottom: 0;
}

@media screen and (max-width: 768px) {
  .stats-container {
    flex-direction: column;
  }

  .board-and-moves-container {
    flex-direction: column;
  }

  .board-container {
    width: 100%;
  }

  .moves-container {
    width: 100%;
    max-height: 200px;
  }
}

pre.pgn-text,
pre.moves-text {
  white-space: pre-wrap;
}
</style>
